// Copyright 2025 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Alert, Modal, QRCode, Spin} from "antd";
import {ArrowLeftOutlined, CheckOutlined, GiftOutlined, LoadingOutlined, SafetyOutlined} from "@ant-design/icons";
import * as Conf from "../Conf";
import * as Setting from "../Setting";
import * as AuthBackend from "./AuthBackend";
import * as UserBackend from "../backend/UserBackend";
import * as OrganizationBackend from "../backend/OrganizationBackend";
import * as Util from "./Util";
import {CaptchaModal} from "../common/modal/CaptchaModal";
import {AGREEMENT_UPDATED_AT, PRIVACY_TITLE, PrivacyContent, TERMS_TITLE, TermsContent} from "./ProcurementAgreements";
import "./ProcurementLoginPage.css";

// Fixed copyright shown at the bottom of the card.
const COPYRIGHT_TEXT = "© 2026 北京鼎函云枢科技有限公司";

function getInviteCodeFromSearch(search = window.location.search) {
  const params = new URLSearchParams(search);
  return params.get("inviteCode") ?? "";
}

// Dedicated login/signup-combined page for the organization configured in Conf.CustomLoginOrg.
// First-time login = registration:
//   - WeChat scan uses the existing WeChat provider with autoSignup enabled (backend already supports it).
//   - Phone verification code relies on the backend auto-register branch gated to this organization.
// All authentication goes through the existing Casdoor backend endpoints so OAuth/MFA behavior matches.
class ProcurementLoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      application: undefined,
      msg: null,
      error: null,
      tab: "wechat", // "wechat" | "sms"
      wechatStep: "qr", // "qr" | "bind" — new WeChat users bind a phone before the account is created
      qrCode: null,
      qrStatus: "loading",
      ticket: null,
      phone: "",
      countryCode: "CN",
      code: "",
      inviteCode: getInviteCodeFromSearch(),
      loginLoading: false,
      codeLeftTime: 0,
      codeSending: false,
      captchaVisible: false,
      agreementType: null, // null | "terms" | "privacy"
      getVerifyTotp: undefined,
    };
    this.pollingTimer = null;
    this.countdownTimer = null;
  }

  // The organization actually being visited (/login/:owner). All listed custom-login orgs share this
  // page, so read the owner from the route instead of hard-coding a single organization.
  getOrg() {
    return this.props.match?.params?.owner ?? Conf.CustomLoginOrg;
  }

  componentDidMount() {
    // Mark the document so this page's CSS can hide the global App footer ("Powered by Casdoor")
    // and remove the default scroll, without touching any other organization's pages.
    document.body.classList.add("pa-active");

    // Quick visual preview: `/login/<org>?preview=1` renders the full page with stub data, so the
    // design can be viewed without configuring the org/app/providers in casdoor.
    if (this.isPreview()) {
      this.loadPreview();
      return;
    }

    OrganizationBackend.getDefaultApplication("admin", this.getOrg()).then((res) => {
      if (res.status === "ok") {
        const application = res.data;
        this.props.onUpdateApplication?.(application);
        // checkLoginMfa()/renderMfaAuthVerifyForm() read the application off props, so expose it there too.
        this.props.application = application;
        this.setState({
          application: application,
        }, () => {
          if (this.getWechatProvider()) {
            this.fetchQrCode();
          } else {
            this.setState({tab: "sms"});
          }
        });
      } else {
        this.props.onUpdateApplication?.(null);
        this.setState({application: null, msg: res.msg});
      }
    });
  }

  componentWillUnmount() {
    document.body.classList.remove("pa-active");
    this.clearPolling();
    this.clearCountdown();
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  startCountdown() {
    const total = (this.state.application?.codeResendTimeout > 0) ? this.state.application.codeResendTimeout : 60;
    this.setState({codeLeftTime: total});
    this.clearCountdown();
    this.countdownTimer = setInterval(() => {
      this.setState((s) => {
        if (s.codeLeftTime <= 1) {
          this.clearCountdown();
          return {codeLeftTime: 0};
        }
        return {codeLeftTime: s.codeLeftTime - 1};
      });
    }, 1000);
  }

  onSendCodeClick() {
    if (!Setting.isValidPhone(this.state.phone, this.state.countryCode)) {
      this.setState({error: "请输入正确的 11 位手机号"});
      return;
    }
    // Preview mode has no backend; just run the countdown so the button state can be seen.
    if (this.isPreview()) {
      this.startCountdown();
      return;
    }
    this.setState({captchaVisible: true, error: null});
  }

  onCaptchaOk(captchaType, captchaToken, clientSecret) {
    const application = this.state.application;
    this.setState({captchaVisible: false, codeSending: true});
    UserBackend.sendCode(captchaType, captchaToken, clientSecret, "login", this.state.countryCode, this.state.phone, "phone", Setting.getApplicationName(application))
      .then((res) => {
        this.setState({codeSending: false});
        if (res) {
          this.startCountdown();
        }
      })
      .catch(() => {
        this.setState({codeSending: false});
      });
  }

  isPreview() {
    return new URLSearchParams(window.location.search).get("preview") !== null;
  }

  // Stub application + fake QR so the whole page (both tabs, animations) renders with no backend config.
  loadPreview() {
    const application = {
      name: "preview-app",
      organization: this.getOrg(),
      displayName: "采购助手",
      logo: "",
      providers: [{provider: {owner: "admin", name: "wechat-preview", type: "WeChat"}}],
      organizationObj: {displayName: "采购助手", countryCodes: ["CN"], logo: ""},
    };
    this.setState({
      application: application,
      countryCode: "CN",
      qrCode: "https://door.casdoor.com/login/procurement-assistant?preview=1",
      qrStatus: "active",
    });
  }

  clearPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  getWechatProvider() {
    return this.state.application?.providers?.find((p) => p.provider?.type === "WeChat")?.provider ?? null;
  }

  fetchQrCode() {
    if (this.isPreview()) {
      return;
    }
    const provider = this.getWechatProvider();
    if (!provider) {
      return;
    }
    this.setState({qrStatus: "loading", qrCode: null, ticket: null});
    AuthBackend.getWechatQRCode(`${provider.owner}/${provider.name}`).then((res) => {
      if (res.status === "ok" && res.data) {
        const ticket = res.data2;
        this.setState({qrCode: res.data, qrStatus: "active", ticket: ticket});
        this.clearPolling();
        this.pollingTimer = setInterval(() => {
          AuthBackend.getWechatMessageEvent(ticket).then((r) => {
            if (r.status === "ok" && (r.data === "SCAN" || r.data === "subscribe")) {
              this.clearPolling();
              this.handleWechatScanned(ticket);
            }
          });
        }, 1000);
      } else {
        this.setState({qrCode: null, qrStatus: "expired", ticket: null});
        this.clearPolling();
      }
    }).catch(() => {
      this.setState({qrCode: null, qrStatus: "expired", ticket: null});
      this.clearPolling();
    });
  }

  // A scan produces an official-account openid held server-side (WechatCacheMap[ticket]).
  // We call /api/login with autoSignup=false so an unknown scanner is NOT auto-created:
  //   - existing user  -> logged in, complete the SSO code redirect;
  //   - new user       -> backend stashes the openid in the session (wechat_user_info) and returns
  //                       "need_sign_up_or_bind"; we switch the card to the phone-binding step. The
  //                       subsequent phone-code login (type=bind/code) drains that session and links
  //                       user.WeChat = openid, so the account is created AND bound in one shot.
  handleWechatScanned(ticket) {
    const application = this.state.application;
    const provider = this.getWechatProvider();
    if (!application || !provider) {
      return;
    }
    const oAuthParams = Util.getOAuthGetParameters();
    const body = {
      type: oAuthParams?.responseType ?? "login",
      application: application.name,
      organization: application.organization,
      provider: provider.name,
      code: `wechat_oa:${ticket}`,
      state: application.name,
      redirectUri: `${window.location.origin}/callback`,
      method: "signup",
      autoSignup: false,
    };
    const inviteCode = this.state.inviteCode.trim();
    if (inviteCode) {
      body.tag = inviteCode;
    }

    AuthBackend.login(body, oAuthParams).then((res) => {
      if (res.status === "ok") {
        const loginHandler = (res) => {
          if (body.type === "code") {
            this.postCodeLogin(res, oAuthParams);
          } else {
            Setting.showMessage("success", "登录成功");
            this.props.onLoginSuccess();
          }
        };
        Setting.checkLoginMfa(res, body, oAuthParams, loginHandler, this);
      } else if (res.data === "need_sign_up_or_bind") {
        this.setState({wechatStep: "bind", error: null});
      } else {
        this.setState({error: res.msg});
        this.fetchQrCode();
      }
    });
  }

  backToQr() {
    this.setState({wechatStep: "qr", error: null});
    this.fetchQrCode();
  }

  switchTab(tab) {
    this.setState({tab: tab, error: null}, () => {
      if (tab === "wechat" && this.state.wechatStep === "qr" && !this.pollingTimer && !this.isPreview()) {
        this.fetchQrCode();
      }
    });
  }

  postCodeLogin(res, oAuthParams) {
    if (res.data3) {
      sessionStorage.setItem("signinUrl", window.location.pathname + window.location.search);
      Setting.goToLinkSoft(this, `/forget/${this.state.application.name}`);
      return;
    }
    const code = res.data;
    const concatChar = oAuthParams?.redirectUri?.includes("?") ? "&" : "?";
    const redirectUrl = `${oAuthParams.redirectUri}${concatChar}code=${code}&state=${oAuthParams.state}`;
    Setting.goToLink(redirectUrl);
  }

  handleSmsSubmit(e, isBind = false) {
    e.preventDefault();
    this.setState({error: null});

    const application = this.state.application;
    if (!Setting.isValidPhone(this.state.phone, this.state.countryCode)) {
      this.setState({error: "请输入正确的 11 位手机号"});
      return;
    }
    if (!this.state.code) {
      this.setState({error: "请输入短信验证码"});
      return;
    }

    const oAuthParams = Util.getOAuthGetParameters();
    // In the WeChat bind flow the openid sits in the server session; type must be "bind" or "code"
    // for the backend to drain it and link user.WeChat. Fall back to "bind" (not "login") when binding.
    const type = oAuthParams?.responseType ?? (isBind ? "bind" : "login");
    const values = {
      application: application.name,
      organization: application.organization,
      username: this.state.phone,
      countryCode: this.state.countryCode,
      code: this.state.code,
      signinMethod: "Verification code",
      autoSignin: false,
      language: Setting.getLanguage() ?? "",
      type: type,
    };
    const inviteCode = this.state.inviteCode.trim();
    if (inviteCode) {
      values.tag = inviteCode;
    }

    this.setState({loginLoading: true});
    AuthBackend.login(values, oAuthParams).then((res) => {
      const loginHandler = (res) => {
        if (values.type === "code") {
          this.postCodeLogin(res, oAuthParams);
        } else {
          Setting.showMessage("success", "登录成功");
          this.props.onLoginSuccess();
        }
      };

      if (res.status === "ok") {
        Setting.checkLoginMfa(res, values, oAuthParams, loginHandler, this);
      } else {
        this.setState({error: `登录失败：${res.msg}`});
      }
    }).finally(() => {
      this.setState({loginLoading: false});
    });
  }

  renderBackgroundArt() {
    return (
      <React.Fragment>
        <div className="pa-bg" />
        <div className="pa-blob pa-blob-1" />
        <div className="pa-blob pa-blob-2" />
        <div className="pa-blob pa-blob-3" />
        <svg className="pa-orbit-left" viewBox="0 0 400 400" fill="none" aria-hidden>
          <defs>
            <linearGradient id="pa-left-a" x1="0" y1="0" x2="400" y2="400" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#38bdf8" stopOpacity="0.55" />
              <stop offset="1" stopColor="#38bdf8" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="pa-left-b" x1="400" y1="0" x2="0" y2="400" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#3b82f6" stopOpacity="0.5" />
              <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="pa-left-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#3b82f6" stopOpacity="0.32" />
              <stop offset="1" stopColor="#3b82f6" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g className="pa-breath">
            <circle cx="200" cy="200" r="130" fill="url(#pa-left-glow)" />
            <circle cx="200" cy="200" r="58" stroke="#3b82f6" strokeOpacity="0.28" strokeWidth="1" />
          </g>
          <g className="pa-orbit-slow">
            <ellipse cx="200" cy="200" rx="180" ry="95" transform="rotate(15 200 200)" stroke="url(#pa-left-a)" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="260 220" />
          </g>
          <g className="pa-orbit-slower">
            <ellipse cx="200" cy="200" rx="160" ry="160" transform="rotate(-25 200 200)" stroke="url(#pa-left-b)" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="210 420" />
          </g>
        </svg>
        <svg className="pa-orbit-right" viewBox="0 0 400 400" fill="none" aria-hidden>
          <defs>
            <linearGradient id="pa-right-a" x1="0" y1="0" x2="400" y2="400" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#a78bfa" stopOpacity="0.55" />
              <stop offset="1" stopColor="#a78bfa" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="pa-right-b" x1="400" y1="0" x2="0" y2="400" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#38bdf8" stopOpacity="0.5" />
              <stop offset="1" stopColor="#38bdf8" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="pa-right-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#a78bfa" stopOpacity="0.35" />
              <stop offset="1" stopColor="#a78bfa" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g className="pa-breath" style={{animationDelay: "2s"}}>
            <circle cx="200" cy="200" r="120" fill="url(#pa-right-glow)" />
            <circle cx="200" cy="200" r="60" stroke="#a78bfa" strokeOpacity="0.28" strokeWidth="1" />
          </g>
          <g className="pa-orbit-slower">
            <ellipse cx="200" cy="200" rx="170" ry="90" transform="rotate(-20 200 200)" stroke="url(#pa-right-a)" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="240 200" />
          </g>
          <g className="pa-orbit-slow">
            <ellipse cx="200" cy="200" rx="150" ry="150" transform="rotate(35 200 200)" stroke="url(#pa-right-b)" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="200 400" />
          </g>
        </svg>
        <div className="pa-streak" />
      </React.Fragment>
    );
  }

  renderHero() {
    const application = this.state.application;
    // Use the logo configured on the organization first, as requested.
    const logoUrl = application?.organizationObj?.logo || application?.logo;
    return (
      <div className="pa-hero">
        <div className="pa-logo-ring">
          <div className="pa-logo-halo" />
          <svg className="pa-logo-rings" viewBox="0 0 96 96" fill="none" aria-hidden>
            <g className="pa-logo-spin">
              <circle cx="48" cy="48" r="46" stroke="currentColor" strokeOpacity="0.22" strokeWidth="1" strokeDasharray="2 4" />
            </g>
            <circle cx="48" cy="48" r="40" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" />
            <g className="pa-logo-spin-reverse">
              <path d="M48 8 A40 40 0 0 1 88 48" stroke="url(#pa-logo-arc)" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d="M48 88 A40 40 0 0 1 8 48" stroke="url(#pa-logo-arc-2)" strokeWidth="2" strokeLinecap="round" fill="none" />
            </g>
            <defs>
              <linearGradient id="pa-logo-arc" x1="48" y1="8" x2="88" y2="48" gradientUnits="userSpaceOnUse">
                <stop stopColor="#38bdf8" />
                <stop offset="1" stopColor="#3b82f6" />
              </linearGradient>
              <linearGradient id="pa-logo-arc-2" x1="48" y1="88" x2="8" y2="48" gradientUnits="userSpaceOnUse">
                <stop stopColor="#818cf8" />
                <stop offset="1" stopColor="#22d3ee" />
              </linearGradient>
            </defs>
          </svg>
          <div className="pa-logo-core">
            {logoUrl ? <img src={logoUrl} alt="logo" /> : null}
          </div>
        </div>
        <h1 className="pa-title">云枢智能体</h1>
      </div>
    );
  }

  renderTabs() {
    const tabs = [
      {id: "wechat", label: "微信登录"},
      {id: "sms", label: "验证码登录"},
    ];
    const available = this.getWechatProvider() ? tabs : tabs.filter((t) => t.id === "sms");
    return (
      <div className="pa-tabs">
        {available.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pa-tab ${this.state.tab === t.id ? "pa-tab-active" : ""}`}
            onClick={() => this.switchTab(t.id)}
          >
            {t.label}
            {this.state.tab === t.id ? <span className="pa-tab-underline" /> : null}
          </button>
        ))}
      </div>
    );
  }

  renderTerms(prefix = "注册代表同意") {
    return (
      <p className="pa-terms">
        {prefix}
        <a onClick={(e) => {e.preventDefault(); this.setState({agreementType: "terms"});}}>《用户服务协议》</a>
        与
        <a onClick={(e) => {e.preventDefault(); this.setState({agreementType: "privacy"});}}>《隐私政策》</a>
      </p>
    );
  }

  renderAgreementModal() {
    const type = this.state.agreementType;
    return (
      <Modal
        className="pa-agreement-modal"
        open={type !== null}
        title={type === "privacy" ? PRIVACY_TITLE : TERMS_TITLE}
        footer={null}
        width={640}
        onCancel={() => this.setState({agreementType: null})}
        destroyOnClose
      >
        <p className="pa-agreement-updated">{AGREEMENT_UPDATED_AT}</p>
        {type === "privacy" ? <PrivacyContent /> : <TermsContent />}
      </Modal>
    );
  }

  renderPhoneFields() {
    return (
      <React.Fragment>
        <div className="pa-field">
          <span className="pa-phone-prefix">+86</span>
          <input
            type="tel"
            autoComplete="tel"
            placeholder="请输入手机号"
            value={this.state.phone}
            maxLength={11}
            onChange={(e) => this.setState({phone: e.target.value.replace(/\D/g, "")})}
          />
        </div>

        <div className="pa-field">
          <SafetyOutlined />
          <input
            type="text"
            placeholder="请输入短信验证码"
            value={this.state.code}
            maxLength={6}
            onChange={(e) => this.setState({code: e.target.value.replace(/\D/g, "")})}
          />
          <button
            type="button"
            className="pa-code-btn"
            disabled={this.state.codeLeftTime > 0 || this.state.codeSending}
            onClick={() => this.onSendCodeClick()}
          >
            {this.state.codeLeftTime > 0 ? `${this.state.codeLeftTime}s` : (this.state.codeSending ? "发送中" : "发送验证码")}
          </button>
        </div>

        <div className="pa-field">
          <GiftOutlined />
          <input
            type="text"
            placeholder="请输入邀请码（选填）"
            value={this.state.inviteCode}
            onChange={(e) => this.setState({inviteCode: e.target.value})}
          />
        </div>
      </React.Fragment>
    );
  }

  renderWechatPane() {
    if (this.state.wechatStep === "bind") {
      return this.renderWechatBind();
    }
    const preview = this.isPreview();
    return (
      <div className="pa-pane" key="wechat">
        <div
          className="pa-qr-frame"
          style={preview ? {cursor: "pointer"} : undefined}
          onClick={preview ? () => this.setState({wechatStep: "bind"}) : undefined}
        >
          <span className="pa-qr-corner tl" />
          <span className="pa-qr-corner tr" />
          <span className="pa-qr-corner bl" />
          <span className="pa-qr-corner br" />
          <QRCode bordered={false} status={this.state.qrStatus} value={this.state.qrCode ?? " "} size={180} />
          <div className="pa-qr-scan-wrap">
            <div className="pa-qr-scan-line" />
          </div>
        </div>
        <p className="pa-qr-hint">打开微信 扫一扫 · 关注服务号自动登录</p>
        <div className="pa-mt-20">
          <div className="pa-field">
            <GiftOutlined />
            <input
              type="text"
              placeholder="请输入邀请码（选填）"
              value={this.state.inviteCode}
              onChange={(e) => this.setState({inviteCode: e.target.value})}
            />
          </div>
        </div>
        <div className="pa-hint-list">
          {this.renderTerms()}
        </div>
      </div>
    );
  }

  renderWechatBind() {
    return (
      <form className="pa-pane pa-stack" key="wechat-bind" onSubmit={(e) => this.handleSmsSubmit(e, true)}>
        <div className="pa-scan-success">
          <div className="pa-scan-success-glow" />
          <div className="pa-scan-success-icon"><CheckOutlined /></div>
          <div className="pa-scan-success-text">
            <p className="pa-scan-success-title">扫码成功</p>
            <p className="pa-scan-success-sub">微信授权成功，请绑定手机号完成登录</p>
          </div>
        </div>

        {this.renderPhoneFields()}

        <button type="submit" className="pa-submit" disabled={this.state.loginLoading}>
          {this.state.loginLoading ? <LoadingOutlined /> : null}
          <span>绑定并登录</span>
        </button>

        <button type="button" className="pa-back-link" onClick={() => this.backToQr()}>
          <ArrowLeftOutlined /> 返回二维码
        </button>

        {this.renderTerms("绑定即表示同意")}
        {this.renderCaptchaModal()}
      </form>
    );
  }

  renderSmsPane() {
    return (
      <form className="pa-pane pa-stack" key="sms" onSubmit={(e) => this.handleSmsSubmit(e)}>
        {this.renderPhoneFields()}

        <button type="submit" className="pa-submit" disabled={this.state.loginLoading}>
          {this.state.loginLoading ? <LoadingOutlined /> : null}
          <span>立即登录 / 注册</span>
        </button>

        <p className="pa-note">如手机未注册将自动创建账号并登录</p>
        {this.renderTerms()}
        {this.renderCaptchaModal()}
      </form>
    );
  }

  renderCaptchaModal() {
    const application = this.state.application;
    if (!application || this.isPreview()) {
      return null;
    }
    return (
      <CaptchaModal
        owner={application.owner}
        name={application.name}
        visible={this.state.captchaVisible}
        onOk={(captchaType, captchaToken, clientSecret) => this.onCaptchaOk(captchaType, captchaToken, clientSecret)}
        onCancel={() => this.setState({captchaVisible: false})}
        isCurrentProvider={false}
      />
    );
  }

  renderPanel() {
    if (this.state.getVerifyTotp !== undefined) {
      return this.state.getVerifyTotp();
    }
    return (
      <React.Fragment>
        {this.renderHero()}
        {this.renderTabs()}
        {this.state.error ? (
          <Alert
            description={this.state.error}
            type="error"
            showIcon
            closable
            onClose={() => this.setState({error: null})}
            style={{marginTop: 16}}
          />
        ) : null}
        {this.state.tab === "wechat" ? this.renderWechatPane() : this.renderSmsPane()}
      </React.Fragment>
    );
  }

  render() {
    const application = this.state.application;

    return (
      <div className="pa-root">
        {this.renderBackgroundArt()}
        <div className="pa-card-wrap">
          <div className="pa-card-border">
            <div className="pa-card-gradient" />
            <div className="pa-card-glow" />
            <div className="pa-card">
              <div className="pa-card-ribbon" />
              {application === undefined ? (
                <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "48px 0"}}>
                  <Spin size="large" indicator={<LoadingOutlined style={{fontSize: 32}} spin />} />
                </div>
              ) : application === null ? (
                <Alert description={this.state.msg ?? "抱歉，你访问的页面不存在。"} type="error" showIcon />
              ) : (
                this.renderPanel()
              )}
            </div>
          </div>
          <p className="pa-copyright">{COPYRIGHT_TEXT}</p>
        </div>
        {this.renderAgreementModal()}
      </div>
    );
  }
}

export default ProcurementLoginPage;
